import { useState, useRef, useEffect } from "react";
import { aiAssistant, aiLyrics } from "../lib/ai";
import { Icon } from "./icons";
import { Button, Sheet } from "./ui";

/** Lyrics assistant shown on the song page: translate / explain the lyrics (pt-PT). */
export function LyricsAssistant({ title, lyrics }) {
  const [mode, setMode] = useState(null); // 'traduzir' | 'explicar'
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");
  const [err, setErr] = useState("");

  const run = async (m) => {
    setMode(m); setBusy(true); setErr(""); setOut("");
    try { setOut(await aiLyrics(title, lyrics, m)); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!lyrics || !lyrics.trim()) return null;

  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }} data-testid="lyrics-assistant">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name="sparkles" size={18} color="var(--brand-primary)" />
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>Assistente da letra</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className={`chip ${mode === "traduzir" ? "active" : ""}`} onClick={() => run("traduzir")} disabled={busy} data-testid="lyrics-translate"><Icon name="language" size={14} /> TRADUZIR</button>
        <button className={`chip ${mode === "explicar" ? "active" : ""}`} onClick={() => run("explicar")} disabled={busy} data-testid="lyrics-explain"><Icon name="bulb" size={14} /> EXPLICAR</button>
      </div>
      {busy ? <div className="t-hint" data-testid="lyrics-ai-loading">O assistente está a pensar…</div> : null}
      {err ? <div style={{ color: "var(--warning)", fontSize: 13 }}>{err}</div> : null}
      {out ? <div style={{ whiteSpace: "pre-wrap", color: "var(--on-surface-3)", fontSize: 14, lineHeight: 1.6 }} data-testid="lyrics-ai-output">{out}</div> : null}
    </div>
  );
}

/** Choir assistant modal (used inside the naipe chat). Q&A, pt-PT, not posted to the real chat. */
export function ChatAssistant({ context, onClose }) {
  const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", content: "Olá! Sou o assistente do Urban Lisbon Choir. Em que posso ajudar com o repertório, ensaios ou estudo das vozes?" }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    const next = [...messages, { id: `u-${Date.now()}`, role: "user", content: q }];
    setMessages(next); setText(""); setBusy(true);
    try {
      const reply = await aiAssistant(next.filter((m) => m.role !== "assistant" || next.indexOf(m) > 0).map((m) => ({ role: m.role, content: m.content })), context);
      setMessages((cur) => [...cur, { id: `a-${Date.now()}`, role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((cur) => [...cur, { id: `e-${Date.now()}`, role: "assistant", content: err.message }]);
    } finally { setBusy(false); }
  };

  return (
    <Sheet title="Assistente do coro" onClose={onClose} testid="chat-assistant" wide>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "55vh", overflowY: "auto", paddingRight: 4 }}>
        {messages.map((m) => (
          <div key={m.id} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%", background: m.role === "user" ? "var(--brand-primary)" : "var(--surface-3)", color: m.role === "user" ? "var(--on-brand)" : "var(--on-surface)", padding: "10px 14px", borderRadius: "var(--r-md)", whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }} data-testid={`assistant-msg-${m.role}`}>
            {m.content}
          </div>
        ))}
        {busy ? <div className="t-hint" style={{ alignSelf: "flex-start" }} data-testid="assistant-loading">O assistente está a escrever…</div> : null}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} style={{ display: "flex", gap: 8 }}>
        <input className="input" style={{ flex: 1 }} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva a sua pergunta…" data-testid="assistant-input" />
        <Button className="auto" type="submit" loading={busy} data-testid="assistant-send"><Icon name="send" size={18} color="var(--on-brand)" /></Button>
      </form>
    </Sheet>
  );
}
